<!-- お気に入りクラブ・メール設定コンポーネント -->
<template>
    <div class="regist selectClubs">
        <form method="post" ref="favoriteClubForm" @submit.prevent="submit">
            <input :value="dataCsrf"
                   :name="dataCsrfName"
                   type="hidden">
            <input :value="dataToken"
                   name="token"
                   type="hidden">
            <input v-model="jsonPatchForUserInfo"
                   name="userinfo_patch"
                   type="hidden">

            <p class="aCt">
                お気に入りクラブを選択してください。<br>
                ワンタッチパスID登録しているクラブは、お気に入りクラブを解除できません。
            </p>

            <input v-for="league in leagues" :key="league" type="radio" name="league" :id="'league' + league" :value="league" v-model="selectedLeague" class="hiddenTrig">

            <div class="leagueToggle">
                <ul class="selectLeague">
                    <li v-for="league in leagues" :key="league" :tabindex="0" @keydown.enter="selectLeague(league)">
                        <label :for="'league' + league" 
                               :class="{ active: selectedLeague === league, [league.toLowerCase()]: true }">{{ league }}</label>
                    </li>
                </ul>
                <ul v-for="league in leagues"
                    :class="['clubList', 'setting', league.toLowerCase()]">
                    <li v-for="club in getSpecifiedClubTeams(league)">
                        <div class="club-team">
                            <img :src="emblemImagePath(club.emblemImg)"
                                 :alt="club.teamName">{{ club.teamName }}
                            <label class="favorite-club" :tabindex="getTabIndex(league)"  @keydown.enter="checkFavoriteClub">
                                <input @click="checkFavoriteClub"
                                       v-model="favoriteClubs"
                                       :value="club.clubCode"
                                       name="favorite-status"
                                       type="checkbox">
                                <b :aria-label="`お気に入りクラブが${getArealabel(favoriteClubs, club.clubCode)}`">★</b>
                            </label>
                            <label v-if="club.clubCode !== 'C2' && club.clubCode !== 'G2'" class="mail-setting" :tabindex="getTabIndex(league)" @keydown.enter="checkAcceptContact">
                                <input @click="checkAcceptContact"
                                       v-model="acceptContacts"
                                       :value="club.clubCode"
                                       name="email-subscription"
                                       type="checkbox">
                                <b :aria-label="`メール配信が${getArealabel(acceptContacts, club.clubCode)}`">
                                    <i class="fas fa-envelope"></i>
                                </b>
                            </label>
                        </div>
                    </li>
                </ul>
                <ul class="clubList setting jleague">
                    <li>
                        <label class="mail-setting" :tabindex="0" @keydown.enter="linkAcceptJleagueContact">
                            Ｊリーグからのメールを受け取る
                            <input @click="linkAcceptJleagueContact"
                                   v-model="acceptContacts"
                                   value="JL"
                                   type="checkbox">
                            <b>
                                <i class="fas fa-envelope"></i>
                            </b>
                        </label>
                    </li>
                </ul>
            </div>

            <div class="btnSpace duo">
                <button @click="backToHome"
                        type="button"
                        class="return">メニューに戻る</button>
                <button :disabled="isSubmit"
                        type="submit">登録</button>
            </div>
        </form>
    </div>
</template>

<script>
import dayjs from 'dayjs'
import jsonpatch from 'fast-json-patch'
import { EnumAlertType } from '../../constant'
import { mixinJleague, mixinFavoriteClub, mixinOneTouchPass, EnumContactCode } from '../mixin/utility'

const EnumAlertMessageType = {
    RegisteredOneTouchPass: 'registered-one-touch-pass',
    FavoriteOff: 'favorite-off',
    FavoriteOn: 'favorite-on',
    OnlyFavoriteOn: 'only-favorite-on',
    OnlyFavoriteOff: 'only-favorite-off',
    OnlyClubMailOn: 'only-club-mail-on',
    ClubMailOff: 'club-mail-off',
    JleagueMailOff: 'jleague-mail-off',
    JleagueMailOn: 'jleague-mail-on',
};
Object.freeze(EnumAlertMessageType);

export default {
    name: 'FavoriteClub',
    mixins: [mixinJleague, mixinFavoriteClub, mixinOneTouchPass],
    data: function(){
        return {
            userInfoPatch: {},
            jsonPatchObserver: {},
            jsonPatchForUserInfo: '',
            leagues: ['J1', 'J2', 'J3'],
            acceptContacts: [],
            targetClubTeam: null,
            alertMessageType: '',
            isSubmit: false,
            selectedLeague: 'J1'
        }
    },
    props: {
        dataCsrf: {
            type: String,
            required: true
        },
        dataCsrfName: {
            type: String,
            required: true
        },
        dataToken: {
            type: String,
            required: true
        },
        userinfo: {
            type: Object,
            required: true
        }
    },
    computed: {
        contactCodeObjects: function(){
            // 設定済みの承諾一覧をオブジェクトに変換して返す
            return this.contactCodeToArray.map(function(contacts){
                // `=` でクラブコードとコンタクトコードに分離する
                const codes = contacts.split('=');
                const key = codes[0];
                const values = codes[1].split(',');

                return {
                    clubCode: key,
                    acceptFlag: (values.indexOf(EnumContactCode.NoticeCode) !== -1),
                    storeAcceptFlag: (values.indexOf(EnumContactCode.StoreNoticeCode) !== -1)
                };
            });
        },
        alertType: function(){
            switch (this.alertMessageType) {
                case EnumAlertMessageType.FavoriteOn:
                case EnumAlertMessageType.FavoriteOff:
                case EnumAlertMessageType.OnlyFavoriteOn:
                case EnumAlertMessageType.OnlyFavoriteOff:
                case EnumAlertMessageType.OnlyClubMailOn:
                case EnumAlertMessageType.ClubMailOff:
                case EnumAlertMessageType.JleagueMailOff:
                case EnumAlertMessageType.JleagueMailOn:
                    return EnumAlertType.Info;
                case EnumAlertMessageType.RegisteredOneTouchPass:
                    return EnumAlertType.Error;
                default:
                    return null;
            }
        },
        alertMessage: function(){
            switch (this.alertMessageType) {
                case EnumAlertMessageType.FavoriteOn:
                    return [
                        this.targetClubTeam.teamName,
                        'をお気に入りに追加します。',
                        '<br>',
                        this.targetClubTeam.teamName,
                        'からのお知らせの配信を許可します。'
                    ].join('');
                case EnumAlertMessageType.OnlyFavoriteOn:
                    return [
                        this.targetClubTeam.teamName,
                        'をお気に入りに追加します。'
                    ].join('');
                case EnumAlertMessageType.FavoriteOff:
                    return [
                        this.targetClubTeam.teamName,
                        'をお気に入りから外します。',
                        '<br>',
                        this.targetClubTeam.teamName,
                        'からのお知らせの配信を停止します。'
                    ].join('');
                case EnumAlertMessageType.OnlyFavoriteOff:
                    return [
                        this.targetClubTeam.teamName,
                        'をお気に入りから外します。'
                    ].join('');
                case EnumAlertMessageType.OnlyClubMailOn:
                    return `${this.targetClubTeam.teamName}からのお知らせの配信を許可します。`;
                case EnumAlertMessageType.ClubMailOff:
                    return this.targetClubTeam.teamName + 'からのお知らせの配信を停止します。';
                case EnumAlertMessageType.JleagueMailOff:
                    return 'Ｊリーグからのお知らせとＪリーグオンラインストアからのお知らせの配信を停止します。';
                case EnumAlertMessageType.JleagueMailOn:
                    return 'Ｊリーグからのお知らせとＪリーグオンラインストアからのお知らせの配信を許可します。';
                case EnumAlertMessageType.RegisteredOneTouchPass:
                    return 'ワンタッチパスID登録しているクラブは、お気に入りクラブを解除できません。';
                default:
                    return null;
            }
        }
    },
    created: function(){
        this.userInfoPatch = Object.assign({}, this.userinfo);
        this.jsonPatchObserver = jsonpatch.observe(this.userInfoPatch);

        if (!this.userInfoPatch.club_cd) this.userInfoPatch.club_cd = [];
        if (!this.userInfoPatch.contact_cd) this.userInfoPatch.contact_cd = [];
        if (!this.userInfoPatch.otpmember_view) this.userInfoPatch.otpmember_view = [];

        this.acceptContacts = this.contactCodeObjects.map(function(contacts){
            return contacts.clubCode;
        });
    },
    methods: {
        emblemImagePath: function(file){
            return 'https://auth2.jleague.jp/emblem/' + file;
        },
        selectLeague(league) {
            this.selectedLeague = league;
        },
        getTabIndex(league) {
            // 動的に tabIndex を設定するためのメソッド
            return league === this.selectedLeague ? 0 : -1;
        },
        getArealabel(allowedclubCodes, clubCode) {
            if (allowedclubCodes !== null) {
                if (allowedclubCodes.includes(clubCode)) {
                    return 'オンです';
                } else {
                    return 'オフです';
                }
            }
        },
        /**
         * お気に入りクラブ設定 (スターアイコンをクリックしたとき)
         * @param {MouseEvent} event
         */
        checkFavoriteClub: function(event){
            let target = "";
            let clubCode = "";
            const favoriteClubs = this.favoriteClubs;

            if (event.key === "Enter") {
                const favoriteClubLabel = event.target;
                target = favoriteClubLabel.querySelector('input');
                target.checked = !target.checked;
                clubCode = target.value;
            }else{
                target = event.target;
                clubCode = target.value;
            }
            this.targetClubTeam = this.getClubTeamByClubCode(clubCode);

            // チェックボックスの value が正しいクラブチームの値かチェックする
            this.checkTargetClubTeam(this.targetClubTeam);
            const isAcceptContact = (this.acceptContacts.indexOf(clubCode) !== -1);
            if (target.checked) {
                if (event.key === "Enter") {
                    favoriteClubs.push(clubCode);
                    this.userInfoPatch.club_cd = favoriteClubs.join(',')
                }
                // お気に入りONにしたとき、メール配信もONにする
                if (clubCode === "C2" || clubCode === "G2") {
                    this.alertMessageType = EnumAlertMessageType.OnlyFavoriteOn;
                } else {
                    if (!isAcceptContact) this.acceptContacts.push(clubCode);
                    this.alertMessageType = EnumAlertMessageType.FavoriteOn;
                }
            } else {
                // ワンタッチパスID登録されているか確認する
                if (this.isRegisteredOneTouchPass(clubCode)) {
                    // ワンタッチパスID登録あり
                    // checkboxをONに戻す
                    target.checked = true;
                    this.alertMessageType = EnumAlertMessageType.RegisteredOneTouchPass;
                } else {
                    // ワンタッチパスID登録なし
                    // お気に入りOFFにしたとき、メール配信もOFFにする
                    if (event.key === "Enter") {
                        favoriteClubs.splice(this.favoriteClubs.indexOf(clubCode), 1);
                        this.userInfoPatch.club_cd = favoriteClubs.join(',')
                    }
                    if (isAcceptContact) {
                        this.acceptContacts.splice(this.acceptContacts.indexOf(clubCode), 1);
                        this.alertMessageType = EnumAlertMessageType.FavoriteOff;
                    } else {
                        this.alertMessageType = EnumAlertMessageType.OnlyFavoriteOff;
                    }
                }
            }

            this.showMessage();
        },
        /**
         * メール設定 (メールアイコンをクリックしたとき)
         * @param {MouseEvent} event
         */
        checkAcceptContact: function(event){
            let target = "";
            let clubCode = "";

            if (event.key === "Enter") {
                const acceptContactLabel = event.target;
                target = acceptContactLabel.querySelector('input');
                target.checked = !target.checked;
                clubCode = target.value;
            }else{
                target = event.target;
                clubCode = target.value;
            }
            this.targetClubTeam = this.getClubTeamByClubCode(target.value);

            // チェックボックスの value が正しいクラブチームの値かチェックする
            this.checkTargetClubTeam(this.targetClubTeam);
            const isAcceptContact = (this.acceptContacts.indexOf(clubCode) !== -1);
            let messageType;

            if (target.checked) {
                if (event.key === "Enter") {
                    if (!isAcceptContact) this.acceptContacts.push(clubCode);
                }
                // メール配信ONにしたとき、お気に入りもONにする
                if (this.favoriteClubs.indexOf(clubCode) === -1) {
                    // お気に入りOFFだったとき、お気に入りに追加する
                    let favoriteClubs = this.favoriteClubs
                    favoriteClubs.push(clubCode);
                    this.userInfoPatch.club_cd = favoriteClubs.join(',')
                    messageType = EnumAlertMessageType.FavoriteOn
                } else {
                    // お気に入りONだったとき、コンタクト承諾のみメッセージを表示する
                    messageType = EnumAlertMessageType.OnlyClubMailOn
                }
                // if (this.favoriteClubs.indexOf(clubCode) === -1) this.favoriteClubs.push(clubCode);
            } else {
                // お気に入りは変更しない
                if (event.key === "Enter") {
                    if (isAcceptContact) this.acceptContacts.splice(this.acceptContacts.indexOf(clubCode), 1);
                }
                        
                messageType = EnumAlertMessageType.ClubMailOff
            }

            this.alertMessageType = messageType
            this.showMessage();
        },
        linkAcceptJleagueContact: function(){
            location.href = 'https://login2.jleague.jp/change-userinfo?edit=email_setting&club_cd=JL';
        },
        isRegisteredOneTouchPass: function(clubCode){
            const displayOneTouchList = this.displayOneTouchList();
            return displayOneTouchList.some(function(pass){
                return pass.club_cd === clubCode;
            });
        },
        today: function(){
            return dayjs()
        },
        /**
         * 渡された値が過去の日付のとき true
         * @returns boolean
         */
        isPastEffectiveYmd: function(pass){
            const today = this.today();
            return dayjs(pass.member_effective_end_ymd).isBefore(today, 'date')
        },
        /**
         * `member_effective_end_ymd` が過去の日付のワンタッチパスを除外して返す
         * @returns {*}
         */
        displayOneTouchList: function(){
            return this.oneTouchPassObjects.filter((item) => {
                return !this.isPastEffectiveYmd(item)
            })
        },
        checkTargetClubTeam: function(clubTeam){
            // チェックボックスのvalueの値が存在しないクラブコードのとき
            if (typeof this.targetClubTeam === 'undefined') {
                alert('登録されていないクラブコードが指定されました。');
                window.location.reload();
            }
        },
        generateJsonPatch: function(){
            const self = this;

            return new Promise(function(resolve){
                // お気に入りクラブを設定する
                if (!self.userInfoPatch.club_cd){
                    self.userInfoPatch.club_cd = null;
                }

                // この画面で JLの許諾変更はしないので、元の値を設定する
                const contact_cd = (typeof self.userInfoPatch.contact_cd === 'string')? [self.userInfoPatch.contact_cd]: self.userInfoPatch.contact_cd;
                const jl_accept = contact_cd.find(function(contact){
                    const codes = contact.split('=');
                    const key = codes[0];
                    return (key === "JL");
                });

                // メール配信を設定する
                const contacts = self.acceptContacts.sort().map(function(contact){
                    // JLかつ、元の値がある場合は元の値を返す
                    if (contact === 'JL' && jl_accept) {
                        return jl_accept;
                    }

                    const accepts = [
                        EnumContactCode.NoticeCode
                    ];
                    // Ｊリーグからのメールを受け取るがONのとき、オンラインストアからのお知らせもONにする
                    // if (contact === 'JL') accepts.push(EnumContactCode.StoreNoticeCode);

                    return self.buildContactCode(contact, accepts);
                });
                self.contactCodes = contacts;
                let client_id = "jleague-auth";
                if (typeof jleagueClient !== 'undefined') {
                    if (typeof jleagueClient.client_id !== 'undefined' && jleagueClient.client_id !== '') {
                        client_id = jleagueClient.client_id;
                    }
                }
                self.userInfoPatch["favorite_opportunity"] = client_id;

                let jsonPatchData = jsonpatch.generate(self.jsonPatchObserver)
                jsonPatchData.push(
                    { "op": "add", "path": "/sfdc_necessary_api_call_mode", "value": "1" },
                    { "op": "add", "path": "/sfdc_sync_contact_code", "value": "1" },
                    { "op": "add", "path": "/sfdc_sync_favorite_club", "value": "1" }
                )
                self.jsonPatchForUserInfo = JSON.stringify(jsonPatchData);

                resolve(self.jsonPatchForUserInfo);
            });
        },
        submit: async function(){
            const form = this.$refs.favoriteClubForm;
            this.isSubmit = true;
            await this.generateJsonPatch();

            form.submit();
        },
        showMessage: function(){
            this.$emit('alert-message', this.alertMessage, this.alertType);
        }
    }
}
</script>
